import { isAdmin } from "auth";
import { UserProfile } from "auth/modules/state";

import React, { createContext, FC, ReactNode, useContext, useMemo, useState } from "react";

import Autocomplete from "@mui/material/Autocomplete";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import ButtonBase from "@mui/material/ButtonBase";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import AccountTreeOutlined from "@mui/icons-material/AccountTreeOutlined";
import AdminPanelSettingsOutlined from "@mui/icons-material/AdminPanelSettingsOutlined";
import ApartmentOutlined from "@mui/icons-material/ApartmentOutlined";
import BrushOutlined from "@mui/icons-material/BrushOutlined";
import BuildRounded from "@mui/icons-material/BuildRounded";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardOutlined from "@mui/icons-material/DashboardOutlined";
import DataUsageOutlined from "@mui/icons-material/DataUsageOutlined";
import GroupOutlined from "@mui/icons-material/GroupOutlined";
import HelpOutline from "@mui/icons-material/HelpOutline";
import HomeOutlined from "@mui/icons-material/HomeOutlined";
import InsightsOutlined from "@mui/icons-material/InsightsOutlined";
import InventoryOutlined from "@mui/icons-material/Inventory2Outlined";
import MapOutlined from "@mui/icons-material/MapOutlined";
import MonitorHeartOutlined from "@mui/icons-material/MonitorHeartOutlined";
import QueryStatsOutlined from "@mui/icons-material/QueryStatsOutlined";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import SpeedOutlined from "@mui/icons-material/SpeedOutlined";
import StorefrontOutlined from "@mui/icons-material/StorefrontOutlined";
import TuneOutlined from "@mui/icons-material/TuneOutlined";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import ViewListOutlined from "@mui/icons-material/ViewListOutlined";
import WysiwygOutlined from "@mui/icons-material/WysiwygOutlined";

import { getOrgNavItems, isAdminOnlyItem, mostSpecificMatch, NavDrawerOrganization, NavDrawerRole, pathMatches } from "../utils";

export const STATUS_URL = window.xaStudioConfig?.statusPageUrl || "https://status.xapp.ai"; //__STATUS_PAGE_URL__
export const SUPPORT_URL = window.xaStudioConfig?.documentationUrl || "https://documentation.xapp.ai";

/** The id of the drawer paper, for the hamburger's aria-controls. */
export const NAV_DRAWER_ID = "studio_nav_drawer";

/**
 * Height of the site header the docked drawer sits under: $site-header-height in the components
 * package (_variables.scss), which the header's SiteHeader component uses. Not studio's $header-height.
 */
export const NAV_DRAWER_HEADER_HEIGHT = 82;

/** Widths of the docked drawer. */
export const NAV_DRAWER_DOCKED_WIDTH = 280;
export const NAV_DRAWER_MINI_WIDTH = 68;

/** How many businesses the BUSINESSES section lists before "All businesses". */
export const NAV_DRAWER_BUSINESS_LIMIT = 5;

export interface NavDrawerOrgOption {
    readonly organizationId: string;
    readonly name: string;
    readonly logoUrl?: string;
}

export interface NavDrawerBusiness {
    readonly appId: string;
    readonly organizationId: string;
    readonly name: string;
    readonly status?: string;
    /** The business's own logo; the storefront icon stands in when there is none. */
    readonly logoUrl?: string;
}

export interface NavDrawerProps {
    readonly open: boolean;
    readonly role: NavDrawerRole;
    readonly profile: UserProfile;
    readonly asUser?: boolean;
    /** The current organization. Absent when the user has several and none is selected. */
    readonly organization?: NavDrawerOrganization;
    /** Org name for the business card, when the org itself cannot be read. */
    readonly organizationName?: string;
    /** Organizations offered by the picker. */
    readonly organizations?: readonly NavDrawerOrgOption[];
    /** Businesses in the current organization (or the ones a business user can reach). */
    readonly businesses?: readonly NavDrawerBusiness[];
    onClose(): void;
    onNavClick(path: string): void;
    onExternalClick(url: string): void;
    /** Recently visited organizations, shown when no organization is selected (e.g. on "/"). */
    readonly recentOrganizations?: readonly NavDrawerOrgOption[];
    /** Recently visited businesses, shown when no organization is selected. */
    readonly recentBusinesses?: readonly (NavDrawerBusiness & { organizationName?: string })[];
    /** How many businesses the current organization has (for this user); All businesses needs more than one. */
    readonly businessesTotal?: number;
    /** How many businesses the user can reach across organizations, for when none is selected. */
    readonly allBusinessesTotal?: number;
    /** The user can reach more than one organization (not just the ones the current search matched). */
    readonly hasMultipleOrganizations?: boolean;
    /** The organization picker is searching. */
    readonly orgSearchLoading?: boolean;
    onSelectOrganization(organizationId: string): void;
    /** The picker reports what they type so the container can search the server. */
    onOrgSearchChange?(search: string): void;
    /**
     * Docked beside the page (wide screens) instead of overlaying it. A docked drawer stays open
     * as they navigate; `open` false means hidden.
     */
    readonly docked?: boolean;
    /** Docked and collapsed to icons. */
    readonly mini?: boolean;
    onCollapse?(): void;
    onExpand?(): void;
    /** The current route, to highlight where they are. */
    readonly currentPath?: string;
}

/** Items render as icons with tooltips when the docked drawer is collapsed. */
const MiniContext = createContext<boolean>(false);

const ORG_ITEM_ICONS: Record<string, ReactNode> = {
    overview: <DashboardOutlined />,
    dashboard: <QueryStatsOutlined />,
    map: <MapOutlined />,
    leads: <InventoryOutlined />,
    management: <TuneOutlined />,
    analytics: <InsightsOutlined />,
    "form-conversion": <WysiwygOutlined />,
    members: <GroupOutlined />,
    settings: <SettingsOutlined />,
    "partner-branding": <BrushOutlined />,
};

/** Every admin page (AdminSwitch's routes), for the drawer's ADMIN section. */
const ADMIN_ITEMS: { label: string; path: string; icon: ReactNode; exact?: boolean }[] = [
    { label: "Admin Home", path: "/admin", icon: <AdminPanelSettingsOutlined />, exact: true },
    { label: "Organizations", path: "/admin/orgs", icon: <ApartmentOutlined /> },
    { label: "Businesses / Locations", path: "/admin/apps", icon: <ViewListOutlined /> },
    { label: "Dashboard", path: "/admin/dashboard", icon: <QueryStatsOutlined /> },
    { label: "Leads", path: "/admin/leads", icon: <InventoryOutlined /> },
    { label: "Map", path: "/admin/map", icon: <MapOutlined /> },
    { label: "Form Conversion", path: "/admin/forms", icon: <WysiwygOutlined /> },
    { label: "Lead Rate Health", path: "/admin/lead-rate-health", icon: <SpeedOutlined /> },
    { label: "Usage", path: "/admin/usage", icon: <DataUsageOutlined /> },
    { label: "Graph", path: "/admin/graph", icon: <AccountTreeOutlined /> },
];

interface ItemProps {
    readonly icon?: ReactNode;
    readonly label: string;
    readonly secondary?: string;
    /** This is the page (or business) they are on. */
    readonly selected?: boolean;
    /** Only admins see this item: a small wrench badge on the icon, in place of the old 🛠️ labels. */
    readonly adminOnly?: boolean;
    onClick(): void;
}

const ItemIcon: FC<{ icon?: ReactNode; adminOnly?: boolean }> = ({ icon, adminOnly }) => (
    <Box component="span" sx={{ position: "relative", display: "inline-flex" }}>
        {icon}
        {adminOnly && (
            <Box
                component="span"
                data-admin-only
                aria-hidden="true"
                sx={{
                    position: "absolute",
                    right: -9,
                    bottom: -7,
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    // Studio's blue with a solid white wrench: the 🛠️ emoji rendered too small and grey
                    // (especially on Windows) to read at this size.
                    bgcolor: "#3295d2",
                    boxShadow: "0 0 0 1.5px #fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    lineHeight: 1,
                }}
            >
                <BuildRounded sx={{ fontSize: 11 }} />
            </Box>
        )}
    </Box>
);

const Item: FC<ItemProps> = ({ icon, label, secondary, selected, adminOnly, onClick }) => {
    const mini = useContext(MiniContext);
    const current = selected ? { "aria-current": "page" as const } : {};

    if (mini) {
        return (
            <Tooltip title={secondary ? `${label} (${secondary})` : label} placement="right">
                <ListItemButton
                    data-nav-item
                    aria-label={label}
                    selected={selected}
                    {...current}
                    onClick={onClick}
                    sx={{ py: 1, justifyContent: "center" }}
                >
                    <ListItemIcon sx={{ minWidth: 0 }}>
                        <ItemIcon icon={icon || <StorefrontOutlined />} adminOnly={adminOnly} />
                    </ListItemIcon>
                </ListItemButton>
            </Tooltip>
        );
    }

    return (
        <ListItemButton data-nav-item selected={selected} {...current} onClick={onClick} sx={{ py: 0.5 }}>
            {icon && (
                <ListItemIcon sx={{ minWidth: 36 }}>
                    <ItemIcon icon={icon} adminOnly={adminOnly} />
                </ListItemIcon>
            )}
            <ListItemText primary={label} secondary={secondary} primaryTypographyProps={{ variant: "body2" }} />
        </ListItemButton>
    );
};

interface SectionProps {
    readonly name: string;
    readonly title?: string;
    readonly label?: string;
    readonly children: ReactNode;
}

/** A labelled group of items. Titled sections are labelled by their heading. */
const Section: FC<SectionProps> = ({ name, title: fullTitle, label, children }) => {
    const mini = useContext(MiniContext);
    // Collapsed: no headings, the section is labelled instead.
    const title = mini ? undefined : fullTitle;
    const headingId = `nav_drawer_${name}_heading`;

    return (
        <List
            dense
            data-nav-section={name}
            aria-labelledby={title ? headingId : undefined}
            aria-label={title ? undefined : label || fullTitle}
            subheader={
                title ? (
                    <ListSubheader id={headingId} disableSticky sx={{ lineHeight: "32px", letterSpacing: "0.08em" }}>
                        {title}
                    </ListSubheader>
                ) : undefined
            }
        >
            {children}
        </List>
    );
};

interface OrgPickerProps {
    readonly organization?: NavDrawerOrganization;
    readonly organizations: readonly NavDrawerOrgOption[];
    readonly loading?: boolean;
    onSelect(organizationId: string): void;
    onSearchChange?(search: string): void;
}

/**
 * Searchable organization picker. What they type is held here, so search results
 * arriving (and the drawer re-rendering) never reset it mid-word.
 */
const OrgPicker: FC<OrgPickerProps> = ({ organization, organizations, loading, onSelect, onSearchChange }) => {
    const currentId = organization?.organizationId;
    const currentName = organization?.name;

    // A stable value: MUI resets the input whenever the value changes identity.
    const current = useMemo<NavDrawerOrgOption | null>(
        () => (currentId ? { organizationId: currentId, name: currentName || currentId } : null),
        [currentId, currentName]
    );

    const [inputValue, setInputValue] = useState<string>(currentName || "");

    // Keep the current org selectable even when a search has filtered it out of the list.
    const options = useMemo(
        () =>
            current && !organizations.some((org) => org.organizationId === current.organizationId)
                ? [current, ...organizations]
                : [...organizations],
        [current, organizations]
    );

    return (
        <Autocomplete<NavDrawerOrgOption, false, true>
            size="small"
            options={options}
            // null, never undefined: on "/" there is no current org, and an undefined value after a
            // defined one leaves MUI in controlled mode comparing options against undefined (crash).
            value={current as NavDrawerOrgOption}
            inputValue={inputValue}
            disableClearable
            loading={loading}
            // The server does the matching; filtering again here would hide ID matches.
            filterOptions={onSearchChange ? (opts) => opts : undefined}
            getOptionLabel={(option) => option.name || option.organizationId}
            isOptionEqualToValue={(option, value) => !!value && option.organizationId === value.organizationId}
            noOptionsText="No organizations found"
            loadingText="Searching…"
            onChange={(_event, value) => {
                if (value && value.organizationId !== currentId) {
                    onSelect(value.organizationId);
                }
            }}
            onInputChange={(_event, value, reason) => {
                setInputValue(value);
                if (reason === "input") {
                    onSearchChange?.(value);
                }
            }}
            onClose={() => {
                // Leaving without picking: show the current org again and drop the search.
                setInputValue(currentName || "");
                onSearchChange?.("");
            }}
            renderOption={(optionProps, option) => (
                <li {...optionProps} key={option.organizationId} data-org-option={option.organizationId}>
                    <Avatar
                        src={option.logoUrl || undefined}
                        alt=""
                        variant="rounded"
                        sx={{ width: 24, height: 24, mr: 1.5, bgcolor: "transparent", color: "action.active" }}
                        imgProps={{ style: { objectFit: "contain" } }}
                    >
                        <ApartmentOutlined fontSize="small" />
                    </Avatar>
                    {option.name || option.organizationId}
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Organization"
                    placeholder="Search organizations"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading && (
                                    <CircularProgress
                                        data-org-search-busy
                                        color="inherit"
                                        size={16}
                                        aria-label="Searching organizations"
                                    />
                                )}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};

/** The business logo, or the storefront icon when it has none or the image fails to load. */
const BusinessLogo: FC<{ business: NavDrawerBusiness; size?: number }> = ({ business, size = 24 }) => (
    <Avatar
        src={business.logoUrl || undefined}
        alt=""
        variant="rounded"
        sx={{ width: size, height: size, bgcolor: "transparent", color: "action.active" }}
        imgProps={{ style: { objectFit: "contain" } }}
    >
        <StorefrontOutlined fontSize="small" />
    </Avatar>
);

function businessPath(business: NavDrawerBusiness): string {
    return `/${business.organizationId}/${business.appId}`;
}

export const NavDrawer: FC<NavDrawerProps> = (props) => {
    const {
        open,
        role,
        profile,
        asUser,
        organization,
        organizationName,
        organizations = [],
        businesses = [],
        onClose,
        onNavClick,
        onExternalClick,
        onSelectOrganization,
        onOrgSearchChange,
        orgSearchLoading,
        hasMultipleOrganizations,
        recentOrganizations = [],
        recentBusinesses = [],
        businessesTotal,
        allBusinessesTotal,
        docked,
        mini: miniProp,
        onCollapse,
        onExpand,
        currentPath,
    } = props;
    const mini = !!docked && !!miniProp;

    // An org with one business: its cross-business pages (the org dashboard, org-wide leads) only
    // repeat that business, split "by business", so leave them out.
    const singleBusinessOrg = !!organization && businessesTotal === 1;
    const orgItems = useMemo(
        () =>
            (organization && profile ? getOrgNavItems({ organization, profile, asUser }) : []).filter(
                (item) => !singleBusinessOrg || !["overview", "dashboard", "leads"].includes(item.key)
            ),
        [organization, profile, asUser, singleBusinessOrg]
    );

    // eject if there is no user profile loaded yet
    if (!profile) {
        return null;
    }

    const isSuperAdmin = role === "super_admin" && isAdmin(profile);
    const isBusinessUser = role === "business";
    const multiOrg = hasMultipleOrganizations ?? organizations.length > 1;
    // With a single business and no other orgs, "/" is just that business.
    const onlyOneBusiness = !multiOrg && (organization ? singleBusinessOrg : allBusinessesTotal === 1);

    function go(path: string) {
        // A docked drawer stays put; an overlay gets out of the way.
        if (!docked) {
            onClose();
        }
        onNavClick(path);
    }

    function renderAdminSection() {
        if (!isSuperAdmin) {
            return null;
        }

        return (
            <Section name="admin" title="ADMIN">
                {ADMIN_ITEMS.map(({ label, path, icon, exact }) => (
                    <Item key={path} icon={icon} label={label} selected={pathMatches(currentPath, path, exact)} onClick={() => go(path)} />
                ))}
            </Section>
        );
    }

    function renderOrgPicker() {
        if (mini) {
            // Collapsed: the current organization as an icon; picking another means expanding.
            if (!organization) {
                return null;
            }
            return (
                <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                    <Tooltip title={organization.name} placement="right">
                        <IconButton data-org-mini aria-label={organization.name} onClick={onExpand} size="small">
                            <Avatar variant="rounded" sx={{ width: 32, height: 32, bgcolor: "primary.main", fontSize: 16 }}>
                                {(organization.name || "?").charAt(0).toUpperCase()}
                            </Avatar>
                        </IconButton>
                    </Tooltip>
                </Box>
            );
        }

        const canSwitch = isSuperAdmin || (hasMultipleOrganizations ?? organizations.length > 1);

        if (!canSwitch) {
            if (!organization) {
                return null;
            }

            return (
                <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                    <Typography variant="overline" component="p" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                        Organization
                    </Typography>
                    <Typography variant="subtitle1" component="p" data-org-name sx={{ fontWeight: 600 }}>
                        {organization.name}
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={{ px: 2, pt: 2, pb: 1 }} data-org-switcher>
                <OrgPicker
                    organization={organization}
                    organizations={organizations}
                    loading={orgSearchLoading}
                    onSelect={onSelectOrganization}
                    onSearchChange={onOrgSearchChange}
                />
                {(
                <ListItemButton
                    data-all-organizations
                    // Admins see every organization in the admin list; everyone else, the ones they belong to.
                    onClick={() => go(isSuperAdmin ? "/admin/orgs" : "/list")}
                    sx={{ mt: 0.5, px: 0.5, py: 0.25, borderRadius: 1 }}
                >
                    <ListItemText
                        primary="All organizations →"
                        primaryTypographyProps={{ variant: "body2", color: "primary", fontWeight: 600 }}
                    />
                </ListItemButton>
                )}
            </Box>
        );
    }

    function renderOrgSection() {
        if (orgItems.length === 0) {
            return null;
        }

        // The org's own page (Overview) only when on it exactly; a business page is not an org item.
        const activeKey = mostSpecificMatch(
            currentPath,
            orgItems.map((i) => ({ key: i.key, path: i.path, exact: i.key === "overview" }))
        );

        return (
            <Section name="organization" label={`${organization.name} navigation`}>
                {orgItems.map((item) => (
                    <Item
                        key={item.key}
                        icon={ORG_ITEM_ICONS[item.key]}
                        label={item.label}
                        selected={item.key === activeKey}
                        adminOnly={isAdminOnlyItem(item)}
                        onClick={() => go(item.path)}
                    />
                ))}
            </Section>
        );
    }

    function renderBusinessesSection() {
        if (!organization) {
            return null;
        }

        // The org's recently visited businesses first, then the rest.
        const recentHere = recentBusinesses.filter((b) => b.organizationId === organization.organizationId);
        const listed = [
            ...recentHere.map((r) => businesses.find((b) => b.appId === r.appId) || r),
            ...businesses.filter((b) => !recentHere.some((r) => r.appId === b.appId)),
        ];
        // A single business needs no list of them.
        const showAll = (businessesTotal ?? businesses.length) > 1;
        // Named for the org, whoever is looking: it is this org's business list.
        const allLabel = `${organization.name} businesses`;

        return (
            <>
            {mini ? <Divider data-mini-divider sx={{ mx: 1.5, my: 0.5 }} /> : <Divider data-businesses-divider />}
            <Section name="businesses" title="BUSINESSES / LOCATIONS">
                {listed.slice(0, NAV_DRAWER_BUSINESS_LIMIT).map((business) => (
                    <Item
                        key={business.appId}
                        icon={<BusinessLogo business={business} />}
                        label={business.name}
                        selected={pathMatches(currentPath, businessPath(business))}
                        onClick={() => go(businessPath(business))}
                    />
                ))}
                {mini && showAll && (
                    <Tooltip title={allLabel} placement="right">
                        <ListItemButton
                            data-all-businesses
                            aria-label={allLabel}
                            onClick={() => go(`/${organization.organizationId}/apps`)}
                            sx={{ py: 1, justifyContent: "center" }}
                        >
                            <ListItemIcon sx={{ minWidth: 0, color: "primary.main" }}>
                                <ViewListOutlined />
                            </ListItemIcon>
                        </ListItemButton>
                    </Tooltip>
                )}
                {!mini && showAll && (
                    <>
                        <Divider component="li" data-all-businesses-divider sx={{ my: 0.5, mx: 2 }} />
                        <ListItemButton
                            data-all-businesses
                            onClick={() => go(`/${organization.organizationId}/apps`)}
                            sx={{ py: 0.5 }}
                        >
                            <ListItemText
                                primary={`${allLabel} →`}
                                primaryTypographyProps={{ variant: "body2", color: "primary", fontWeight: 600 }}
                            />
                        </ListItemButton>
                    </>
                )}
            </Section>
            </>
        );
    }

    function renderRecents() {
        // Collapsed, bare logos without headings read as the current org, so leave recents out.
        if (organization || isBusinessUser || mini) {
            return null;
        }
        // Admins browse every business in the admin list; others, the ones they can reach.
        const allBusinessesPath = isSuperAdmin ? "/admin/apps" : "/apps";
        const showAllBusinesses = (allBusinessesTotal ?? 0) > 1;

        return (
            <>
                {recentOrganizations.length > 0 && (
                    <Section name="recent-organizations" title="RECENT ORGANIZATIONS">
                        {recentOrganizations.slice(0, NAV_DRAWER_BUSINESS_LIMIT).map((org) => (
                            <Item
                                key={org.organizationId}
                                icon={
                                    <Avatar
                                        src={org.logoUrl || undefined}
                                        alt=""
                                        variant="rounded"
                                        sx={{ width: 24, height: 24, bgcolor: "transparent", color: "action.active" }}
                                        imgProps={{ style: { objectFit: "contain" } }}
                                    >
                                        <ApartmentOutlined fontSize="small" />
                                    </Avatar>
                                }
                                label={org.name || org.organizationId}
                                onClick={() => go(`/${org.organizationId}`)}
                            />
                        ))}
                    </Section>
                )}
                {(recentBusinesses.length > 0 || showAllBusinesses) && (
                    <Section name="recent-businesses" title="RECENT BUSINESSES">
                        {recentBusinesses.slice(0, NAV_DRAWER_BUSINESS_LIMIT).map((business) => (
                            <Item
                                key={business.appId}
                                icon={<BusinessLogo business={business} />}
                                label={business.name}
                                secondary={business.organizationName}
                                onClick={() => go(businessPath(business))}
                            />
                        ))}
                        {showAllBusinesses && !mini && (
                            <>
                                <Divider component="li" sx={{ my: 0.5, mx: 2 }} />
                                <ListItemButton data-all-businesses onClick={() => go(allBusinessesPath)} sx={{ py: 0.5 }}>
                                    <ListItemText
                                        primary="All businesses →"
                                        primaryTypographyProps={{ variant: "body2", color: "primary", fontWeight: 600 }}
                                    />
                                </ListItemButton>
                            </>
                        )}
                        {showAllBusinesses && mini && (
                            <Item icon={<ViewListOutlined />} label="All businesses" onClick={() => go(allBusinessesPath)} />
                        )}
                    </Section>
                )}
            </>
        );
    }

    function renderBusinessCards() {
        if (mini) {
            return (
                <Section name="businesses" label="Your businesses">
                    {businesses.map((business) => (
                        <Item
                            key={business.appId}
                            icon={<BusinessLogo business={business} />}
                            label={business.name}
                            onClick={() => go(businessPath(business))}
                        />
                    ))}
                </Section>
            );
        }

        return (
            <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                {businesses.map((business) => (
                    <ButtonBase
                        key={business.appId}
                        data-business-card
                        onClick={() => go(businessPath(business))}
                        aria-label={`Open ${business.name}`}
                        sx={{
                            display: "block",
                            textAlign: "left",
                            width: "100%",
                            p: 2,
                            borderRadius: 2,
                            border: 1,
                            borderColor: "divider",
                            "&:hover, &.Mui-focusVisible": { bgcolor: "action.hover" },
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <BusinessLogo business={business} size={36} />
                            <Typography variant="subtitle1" component="p" data-business-name sx={{ fontWeight: 600 }}>
                                {business.name}
                            </Typography>
                        </Box>
                        {business.status && (
                            <Typography variant="body2" component="p" color="text.secondary" data-business-status>
                                {business.status}
                            </Typography>
                        )}
                        {organizationName && (
                            <Typography variant="caption" component="p" color="text.secondary" data-business-org>
                                {organizationName}
                            </Typography>
                        )}
                    </ButtonBase>
                ))}
            </Box>
        );
    }

    const width = mini ? NAV_DRAWER_MINI_WIDTH : NAV_DRAWER_DOCKED_WIDTH;

    function renderTopControls() {
        if (docked) {
            // The top of the docked drawer lines up with the site header and carries the menu controls.
            return (
                <Box
                    data-drawer-header
                    sx={{
                        height: NAV_DRAWER_HEADER_HEIGHT,
                        flex: `0 0 ${NAV_DRAWER_HEADER_HEIGHT}px`,
                        bgcolor: "#061739",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: mini ? "center" : "space-between",
                        px: mini ? 0 : 1,
                        boxSizing: "border-box",
                    }}
                >
                    <Tooltip title={mini ? "Expand menu" : "Collapse to icons"} placement="right">
                        <IconButton
                            aria-label={mini ? "Expand menu" : "Collapse menu to icons"}
                            onClick={mini ? onExpand : onCollapse}
                            sx={{ color: "inherit" }}
                        >
                            {mini ? <MenuIcon /> : <ChevronLeft />}
                        </IconButton>
                    </Tooltip>
                    {!mini && (
                        <Tooltip title="Hide menu">
                            <IconButton aria-label="Hide menu" onClick={onClose} size="small" sx={{ color: "inherit" }}>
                                <VisibilityOffOutlined fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            );
        }
        if (!docked) {
            return (
                <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
                    <IconButton aria-label="Close navigation menu" onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            );
        }
        if (mini) {
            return (
                <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
                    <Tooltip title="Expand menu" placement="right">
                        <IconButton aria-label="Expand menu" onClick={onExpand} size="small">
                            <ChevronRight />
                        </IconButton>
                    </Tooltip>
                </Box>
            );
        }
        return (
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5, p: 1 }}>
                <Tooltip title="Hide menu">
                    <IconButton aria-label="Hide menu" onClick={onClose} size="small">
                        <VisibilityOffOutlined fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Collapse to icons">
                    <IconButton aria-label="Collapse menu to icons" onClick={onCollapse} size="small">
                        <ChevronLeft />
                    </IconButton>
                </Tooltip>
            </Box>
        );
    }

    const content = (
        <MiniContext.Provider value={mini}>
            <Box
                component="nav"
                aria-label="Main navigation"
                sx={{ display: "flex", flexDirection: "column", height: "100%" }}
            >
                {renderTopControls()}

                <Box sx={{ flex: "1 1 auto", overflowY: "auto" }}>
                    {renderAdminSection()}
                    {isSuperAdmin && <Divider />}
                    {!isSuperAdmin && !isBusinessUser && !onlyOneBusiness && multiOrg && (
                        // The cross-organization pages. Admins land on Admin Home at "/", and anyone with a
                        // single organization or business is redirected from "/" to it, so only someone with
                        // several organizations needs these. Named like the organization's own pages.
                        <Section name="root" label="All organizations">
                            <Item icon={<HomeOutlined />} label="Overview" selected={pathMatches(currentPath, "/", true)} onClick={() => go("/")} />
                            <Item icon={<DashboardOutlined />} label="Leads Dashboard" selected={pathMatches(currentPath, "/dashboard")} onClick={() => go("/dashboard")} />
                            <Item icon={<InventoryOutlined />} label="Leads" selected={pathMatches(currentPath, "/leads")} onClick={() => go("/leads")} />
                        </Section>
                    )}
                    {isBusinessUser ? (
                        renderBusinessCards()
                    ) : (
                        <>
                            {renderOrgPicker()}
                            {renderRecents()}
                            {renderOrgSection()}
                            {renderBusinessesSection()}
                        </>
                    )}
                </Box>

                <Divider />
                <Section name="footer" label="Status and help">
                    <Item icon={<MonitorHeartOutlined />} label="Status" onClick={() => onExternalClick(STATUS_URL)} />
                    <Item
                        icon={<HelpOutline />}
                        label="Help & documentation"
                        onClick={() => onExternalClick(SUPPORT_URL)}
                    />
                </Section>
            </Box>
        </MiniContext.Provider>
    );

    if (docked) {
        // Beside the page and the header, both shifted by the same width (see the
        // --studio-nav-width variable set by the header).
        if (!open) {
            return null;
        }
        return (
            <Drawer
                anchor="left"
                variant="permanent"
                open
                PaperProps={{
                    id: NAV_DRAWER_ID,
                    sx: {
                        // Full height: the drawer runs up through the header, which shifts over with the page.
                        top: 0,
                        height: "100%",
                        width,
                        overflowX: "hidden",
                        boxSizing: "border-box",
                        transition: "width 150ms ease",
                    },
                }}
            >
                {content}
            </Drawer>
        );
    }

    return (
        <Drawer
            anchor="left"
            variant="temporary"
            open={open}
            onClose={onClose}
            // No scroll lock: locking the body changes the page width, which sent the org
            // dashboard's responsive recharts into a synchronous resize/render loop that froze
            // the tab. The drawer overlays the page, so the page scrolling underneath is harmless.
            ModalProps={{ disableScrollLock: true }}
            PaperProps={{ id: NAV_DRAWER_ID, sx: { width: 300, maxWidth: "85vw" } }}
        >
            {content}
        </Drawer>
    );
};

